import { useState } from 'react'
import { 
  Edit, 
  Trash2, 
  UserCheck, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  Ruler,
  Settings,
  Eye
} from 'lucide-react'
import { LoadingSpinner } from '../common'
import CustomerMeasurementsModal from './CustomerMeasurementsModal'
import MeasurementPreviewModal from './MeasurementPreviewModal'
import measurementService from '../../services/measurementService'

function CustomerList({ 
  customers, 
  isLoading, 
  onEdit, 
  onDelete, 
  onToggleStatus,
  userRole,
  measurementsCount = {},
  onMeasurementsChange
}) {
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [showMeasurementsModal, setShowMeasurementsModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewMeasurement, setPreviewMeasurement] = useState(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  const handleManageMeasurements = (customer) => {
    setSelectedCustomer(customer)
    setShowMeasurementsModal(true)
  }

  const handleCloseMeasurementsModal = () => {
    setShowMeasurementsModal(false)
    setSelectedCustomer(null)
  }

  const handleMeasurementsChange = () => {
    if (onMeasurementsChange) {
      onMeasurementsChange()
    }
  }

  const handlePreviewMeasurement = async (customer) => {
    const count = measurementsCount[customer.id] || 0
    
    // If only one measurement, show it directly
    if (count === 1) {
      setIsLoadingPreview(true)
      setSelectedCustomer(customer)
      setShowPreviewModal(true)
      
      try {
        // Try to get measurement from customer.measurements if available
        if (customer.measurements && customer.measurements.length === 1) {
          setPreviewMeasurement(customer.measurements[0])
        } else {
          // Otherwise fetch measurements for this customer
          const response = await measurementService.getAll({ 
            customerId: customer.id,
            limit: 1 
          })
          const measurements = response.data?.measurements || response.measurements || []
          if (measurements.length > 0) {
            setPreviewMeasurement(measurements[0])
          }
        }
      } catch (error) {
        console.error('Error fetching measurement:', error)
      } finally {
        setIsLoadingPreview(false)
      }
    } else {
      // If multiple measurements, open management modal
      handleManageMeasurements(customer)
    }
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewMeasurement(null)
    setSelectedCustomer(null)
  }
  if (isLoading) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  if (!customers || customers.length === 0) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">No customers found</h3>
            <p className="mt-1 text-sm text-secondary-500">
              Get started by creating a new customer.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-content p-0">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Customer</th>
                <th className="table-header-cell">Contact</th>
                <th className="table-header-cell">Type</th>
                <th className="table-header-cell">Measurements</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Created</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {customers.map((customer) => (
                <tr key={customer.id} className="table-row">
                  <td className="table-cell">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-base font-medium text-secondary-900">
                          {customer.firstName} {customer.lastName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="space-y-1">
                      {customer.email && (
                        <div className="flex items-center text-base text-secondary-600">
                          <Mail className="h-4 w-4 mr-2" />
                          {customer.email}
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center text-base text-secondary-600">
                          <Phone className="h-4 w-4 mr-2" />
                          {customer.phone}
                        </div>
                      )}
                      {customer.address && (
                        <div className="flex items-start text-base text-secondary-600">
                          <MapPin className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="truncate max-w-xs">
                            {customer.address}
                            {customer.city && `, ${customer.city}`}
                            {customer.state && `, ${customer.state}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      customer.customerType === 'VIP' 
                        ? 'bg-purple-100 text-purple-800' 
                        : customer.customerType === 'Walk-in'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {customer.customerType || 'Regular'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center text-sm text-secondary-900">
                        <Ruler className="h-4 w-4 mr-2 text-primary-600" />
                        <span className="font-medium">
                          {measurementsCount[customer.id] || 0}
                        </span>
                        <span className="text-secondary-500 ml-1">
                          {measurementsCount[customer.id] === 1 ? 'measurement' : 'measurements'}
                        </span>
                      </div>
                      {(measurementsCount[customer.id] || 0) > 0 && (
                        <button
                          onClick={() => handlePreviewMeasurement(customer)}
                          className="text-primary-600 hover:text-primary-900"
                          title={measurementsCount[customer.id] === 1 ? 'View Measurement' : 'View Measurements'}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleManageMeasurements(customer)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Manage Measurements"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      customer.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {customer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center text-sm text-secondary-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onEdit(customer)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit Customer"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onToggleStatus(customer)}
                        className={customer.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}
                        title={customer.isActive ? 'Deactivate Customer' : 'Activate Customer'}
                      >
                        {customer.isActive ? <User className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </button>
                      {/* Only Super Admin can delete customers */}
                      {userRole === 1 && (
                        <button
                          onClick={() => onDelete(customer)}
                          className="text-danger-600 hover:text-danger-900"
                          title="Delete Customer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Measurements Modal */}
      <CustomerMeasurementsModal
        isOpen={showMeasurementsModal}
        customer={selectedCustomer}
        onClose={handleCloseMeasurementsModal}
        userRole={userRole}
        onMeasurementsChange={handleMeasurementsChange}
      />

      {/* Measurement Preview Modal */}
      <MeasurementPreviewModal
        isOpen={showPreviewModal}
        measurement={previewMeasurement}
        customer={selectedCustomer}
        onClose={handleClosePreviewModal}
        isLoading={isLoadingPreview}
      />
    </div>
  )
}

export default CustomerList
